import { HttpClient, HttpEventType } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { WcgUploadReviewerMaterialsComponent } from '@data-access/submissions/dialogs/components/upload-reviewer-materials/upload-reviewer-materials.component';
import { UPLOAD_DIALOG_ACTIONS } from '@documents/actions';
import { select, Store } from '@ngrx/store';
import { UploadReviewerMaterialData } from '@shared/dialogs/types';
import { PermissionsService } from '@shared/services/permissions.service';
import { UtilsService } from '@shared/services/utils.service';

import { UploadStatusItem } from '@documents/models';
import { uniqueId } from 'lodash';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  filter,
  mergeMap,
  Observable,
  of,
  Subscription,
  take,
  tap,
} from 'rxjs';

import environment from '../../../../environments/environment';
import { isDuplicateFileError } from '@admin/questionnaire/utils';
import {
  selectActiveSubmissionId,
  selectActiveSubmissionStage,
  selectMaterialDocumentTypes,
  selectReviewerDocumentById,
  selectReviewMaterialDocuments,
  selectReviewMaterialDocumentsLoading,
} from '../redux/submission.selectors';
import {
  storeLoadingReviewMaterials,
  storeMaterialDocumentTypes,
  storeReviewMaterials,
  updateStoreReviewMaterials,
  updatingReviewerDocumentList,
  uploadingReviewerDocument,
} from '../redux/submission.actions';

@Injectable({
  providedIn: 'root',
})
export class ReviewerMaterialsService {
  private readonly _http = inject(HttpClient);
  private readonly _utils = inject(UtilsService);
  private readonly _store = inject(Store);
  private readonly _permissionsService = inject(PermissionsService);

  getReviewer$: Observable<any> = combineLatest([
    this._permissionsService.isInternal$,
    this._store.select(selectActiveSubmissionId),
    this._store.select(selectActiveSubmissionStage),
  ]).pipe(
    filter(
      ([isInternal, submissionId, stage]) =>
        isInternal !== undefined &&
        submissionId !== undefined &&
        stage !== undefined,
    ),
    tap(([isInternal, submissionId]) => {
      if (isInternal && submissionId) {
        this.getReviewerDocumentsData(submissionId);
      }
    }),
  );

  private documentTypesSubject = new BehaviorSubject<any[]>([]);
  private loadedDocumentTypes = false;
  documentTypes$ = this.documentTypesSubject.asObservable();

  private reviewerDocumentsSubject = new BehaviorSubject<any[]>([]);
  reviewerDocuments$ = this.reviewerDocumentsSubject.asObservable();
  private loadedReviewerDocuments: any = {};

  // Signals for upload tracking
  uploadStatus = signal<Record<number, UploadStatusItem>>({});
  private uploadSubscriptions: Record<number, Subscription> = {};
  private uploadIndexCount = 0;

  getReviewerDocumentsData(submissionId: string): Observable<any[]> {
    this._store.dispatch(storeLoadingReviewMaterials());
    this._http
      .get<any>(
        `${environment.irisApi}/workspace/${submissionId}/reviewer-document/?page=1&page_size=20`,
      )
      .subscribe((documents) => {
        this.loadedReviewerDocuments[submissionId] = documents.data;
        this.reviewerDocumentsSubject.next(documents.data);
        this._store.dispatch(
          storeReviewMaterials({ documents: documents.data }),
        );
      });
    return this.reviewerDocuments$;
  }

  getReviewerDocumentTypesData(): Observable<any[]> {
    if (this.loadedDocumentTypes) {
      return this.documentTypes$;
    }

    this.loadedDocumentTypes = true;

    this._http
      .get<any>(
        `${environment.irisApi}/reviewer-document-type/?page=1&page_size=10`,
      )
      .subscribe((documentTypes) => {
        this._store.dispatch(
          storeMaterialDocumentTypes({
            documentTypes: documentTypes.data,
          }),
        );
        this.documentTypesSubject.next(documentTypes.data);
      });
    return this.documentTypes$;
  }

  toggleCompleteReviewDocument(documentId: string, isComplete: boolean) {
    const body = this._utils.convertKeysToSnakeCase({ isComplete });
    return this._http.put<any>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
      body,
    );
  }

  getReviewerDocument(documentId: string, isInternal: boolean) {
    if (!isInternal) {
      return of([]);
    }

    return combineLatest([
      this._store.select(selectReviewMaterialDocumentsLoading),
      this._store.select(selectReviewMaterialDocuments),
    ]).pipe(
      take(1),
      mergeMap(([loading, documents]) => {
        if (!documents?.length && !loading)
          this.getReviewerDocumentsData(documentId);
        return this._store.select(selectReviewMaterialDocuments);
      }),
    );
  }

  getReviewDocumentTypes(isInternal: boolean) {
    if (!isInternal) {
      return of([]);
    }
    return combineLatest([
      this._store.select(selectMaterialDocumentTypes),
    ]).pipe(
      take(1),
      distinctUntilChanged(),
      mergeMap(([documents]) => {
        if (documents?.length === 0) this.getReviewerDocumentTypesData();
        return this._store.select(selectMaterialDocumentTypes);
      }),
    );
  }

  deleteReviewerDocument(documentId: string) {
    return this._http.delete<IReviewerDocumentMessage>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
    );
  }

  updateReviewDocument(documentId: string, reviewer_document_type_id: string) {
    const body = this._utils.convertKeysToSnakeCase({
      reviewer_document_type_id,
    });
    return this._http.put<any>(
      `${environment.irisApi}/reviewer-document/${documentId}/`,
      body,
    );
  }
  addReviewerMaterialDocuments(document: any) {
    this._store.dispatch(updatingReviewerDocumentList({ document }));
  }

  // New methods for handling uploads with signals
  uploadReviewMaterialWithSignal(
    item: any,
    matchedDocumentType: any,
    workspaceId: string,
    hasDuplicateFile = false,
  ): number {
    const index = this.uploadIndexCount++;

    // Initialize status bar for this upload
    this.updateUploadStatus(index, {
      index,
      isError: '',
      isUploadInProgress: false,
      progressCount: 0,
      uploadFileName: item.file.name,
    });

    const data = new FormData();
    data.append('reviewer_document_type_id', matchedDocumentType?.id || '');
    data.append('name', item.file.name);
    data.append('workspace_id', workspaceId);
    data.append('file', item.file, item.file.name);

    this._store.dispatch(
      uploadingReviewerDocument({
        uuid: uniqueId(),
        filename: item.file.name,
        reviewDocumentTypeId: matchedDocumentType?.id,
        reviewDocumentTypeName: matchedDocumentType?.name,
        workspaceId,
      }),
    );

    const subscription = this._http
      .post(`${environment.irisApi}/reviewer-document/`, data, {
        reportProgress: true,
        observe: 'events',
      })
      .pipe(
        catchError((error) => {
          this.updateUploadStatus(index, {
            ...this.uploadStatus()[index],
            isError: error,
            isUploadInProgress: true,
          });

          if (isDuplicateFileError(error) && !hasDuplicateFile) {
            // Signal to the component that there's a duplicate file
            this.openUploadReviewerMaterialsDialog({
              documentTypes: matchedDocumentType,
              hasDuplicateFile: true,
              multiSelect: true,
              submissionId: workspaceId,
              title: 'Upload Reviewer Materials',
            });
          }

          return EMPTY;
        }),
        tap((event) => {
          switch (event.type) {
            case HttpEventType.UploadProgress:
              if (event.total) {
                const progressCount = Math.round(
                  (100 * event.loaded) / event.total,
                );
                this.updateUploadStatus(index, {
                  ...this.uploadStatus()[index],
                  progressCount,
                  isUploadInProgress: true,
                });
              }
              break;
            case HttpEventType.Response:
              this.cancelUpload(index);

              this._store.dispatch(
                updateStoreReviewMaterials({
                  documents: event.body ? [{ ...event.body }] : [],
                }),
              );
              break;
            default:
              break;
          }
        }),
      )
      .subscribe();

    this.uploadSubscriptions[index] = subscription;
    return index;
  }

  updateUploadStatus(index: number, status: UploadStatusItem): void {
    this.uploadStatus.update((current) => ({
      ...current,
      [index]: status,
    }));
  }

  cancelUpload(index: number): void {
    if (this.uploadSubscriptions[index]) {
      this.uploadSubscriptions[index].unsubscribe();

      this.updateUploadStatus(index, {
        ...this.uploadStatus()[index],
        isUploadInProgress: false,
      });

      delete this.uploadSubscriptions[index];
    }
  }

  checkIfDocumentExists(documentId: string) {
    return this._store.pipe(select(selectReviewerDocumentById(documentId)));
  }

  openUploadReviewerMaterialsDialog(data: UploadReviewerMaterialData) {
    this._store.dispatch(
      UPLOAD_DIALOG_ACTIONS.openReviewerMaterialsDialog({
        component: () => WcgUploadReviewerMaterialsComponent,
        data,
      }),
    );
  }
}

export interface IReviewerDocumentMessage {
  message: string;
}
